import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const STATUS_COLOURS = {
  'In Progress': { bg: '#0d5c3d', dot: '#34d399' },
  'Not Started': { bg: '#8b7944', dot: '#d2c48a' },
  'Completed':   { bg: '#6b2d3c', dot: '#f9a8d4' },
};

function ProjectCard({ project, animationDelay }) {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = useCallback((e) => {
    e.preventDefault();
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Body scroll lock + Escape key
  useEffect(() => {
    if (!modalOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen, closeModal]);

  return (
    <article
      className="project-card"
      style={animationDelay != null ? { animationDelay: `${animationDelay}s` } : undefined}
    >
      <div className="project-card-image-wrap">
        <img
          src={project.image}
          alt={project.title}
          className="project-card-image"
          loading="lazy"
        />
        <span
          className="project-card-status"
          style={{ background: STATUS_COLOURS[project.status]?.bg }}
        >
          <span
            className="project-card-status-dot"
            style={{ background: STATUS_COLOURS[project.status]?.dot }}
          />
          {project.status}
        </span>
      </div>
      <div className="project-card-body">
        <span className="project-card-category">{project.category}</span>
        <h2 className="project-card-title">{project.title}</h2>
        <p className="project-card-description">{project.description}</p>

        {/* Timeline (optional) */}
        {project.timeline && project.timeline.length > 0 && (
          <div className="project-card-timeline" aria-label="Project timeline">
            <h3 className="project-card-timeline-heading">Progress</h3>
            <ol className="project-card-timeline-list">
              {project.timeline.map((step, idx) => (
                <li
                  key={idx}
                  className={`project-card-timeline-step ${
                    step.complete ? 'complete' : ''
                  } ${
                    !step.complete &&
                    (idx === 0 || project.timeline[idx - 1]?.complete)
                      ? 'current'
                      : ''
                  }`}
                >
                  <span className="project-card-timeline-marker" aria-hidden="true">
                    {step.complete ? '✓' : idx + 1}
                  </span>
                  <span className="project-card-timeline-label">{step.label}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Progress link (optional) — opens modal */}
        {project.progressLink && (
          <button
            type="button"
            className="project-card-progress-link"
            onClick={openModal}
            aria-label={`Check the progress of ${project.title} on Facebook`}
          >
            <span className="project-card-progress-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </span>
            Check the Progress
            <span className="project-card-progress-arrow" aria-hidden="true">→</span>
          </button>
        )}

        {/* Facebook embed modal */}
        {modalOpen && project.progressLink && createPortal(
          <div
            className="progress-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={`${project.title} — Facebook progress update`}
            onClick={closeModal}
          >
            <div
              className="progress-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="progress-modal-header">
                <h3 className="progress-modal-title">{project.title}</h3>
                <button
                  type="button"
                  className="progress-modal-close"
                  onClick={closeModal}
                  aria-label="Close"
                  autoFocus
                >
                  ✕
                </button>
              </div>
              <div className="progress-modal-body">
                <div className="progress-modal-preview">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="progress-modal-preview-image"
                  />
                  <div className="progress-modal-preview-content">
                    <span className="progress-modal-preview-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877f2">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook Video Update
                    </span>
                    <p className="progress-modal-preview-desc">
                      Watch the latest progress video for this project on our Facebook page. See the craftsmanship in action!
                    </p>
                  </div>
                </div>

                <a
                  href={project.progressLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="progress-modal-open-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Watch on Facebook
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Disclosure (optional) */}
        {project.disclosure && (
          <details className="project-card-disclosure">
            <summary className="project-card-disclosure-trigger">
              {project.disclosure.trigger}
            </summary>
            <div className="project-card-disclosure-content">
              {project.disclosure.sections.map((section, idx) => (
                <div key={idx} className="project-card-disclosure-section">
                  <h4 className="project-card-disclosure-heading">
                    {section.heading}
                  </h4>
                  {section.body && (
                    <p className="project-card-disclosure-body">{section.body}</p>
                  )}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="project-card-disclosure-list">
                      {section.bullets.map((item, bIdx) => {
                        const colonIdx = item.indexOf(':');
                        if (colonIdx > -1 && colonIdx < 30) {
                          return (
                            <li key={bIdx}>
                              <strong>{item.slice(0, colonIdx + 1)}</strong>
                              {item.slice(colonIdx + 1)}
                            </li>
                          );
                        }
                        return <li key={bIdx}>{item}</li>;
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}

        <div className="project-card-services">
          {project.services.map((s) => (
            <span key={s} className="project-card-tag">
              {s}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default ProjectCard;
